import imglogo from '../../../assets/img/logo_t4i.png'
import bgAuthenticate from '../../../assets/img/bg-authenticate.png'
import React, { useState } from 'react'
import { useForm } from './hooks/useForm'
import '../login.css'
import './signup.css'
import classNames from 'classnames'
import { useHistory } from 'react-router-dom'
import { CustomModal } from '../../../components/CustomModal/CustomModal'
import { SignupLifecycleNotice } from './components/SignupLifecycleNotice'
import useDocumentTitle from '../../../hooks/useDocumentTitle'
import { GoogleContinueButton } from '../staffGoogle/GoogleContinueButton'

const initialForm = {
  name: '',
  companyName: '',
  email: '',
  phone_number: '',
  password: '',
  confirmPassword: '',
  phone_ext: '+1',
}

const REGEX_PASSWORD = new RegExp('^(((?=.*[a-z])(?=.*[A-Z]))|((?=.*[a-z])(?=.*[0-9]))|((?=.*[A-Z])(?=.*[0-9])))(?=.{8,20})')
const REGEX_PERSON_NAME = /^[\p{L}][\p{L} .'-]*$/u
const REGEX_COMPANY_NAME = /^[\p{L}\p{N}][\p{L}\p{N} .,&'’()/+#-]*$/u
const REGEX_EMAIL = /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i
const COMPANY_NAME_MAX_LENGTH = 255

function phoneDigits(value) {
  return String(value || '')
    .replace(/\D/g, '')
    .slice(0, 10)
}

function formatUsPhone(value) {
  const digits = phoneDigits(value)
  if (digits.length < 4) return digits
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}

const validationsForm = (form) => {
  const errors = {}
  const contactName = String(form.name || '').trim()
  const companyName = String(form.companyName || '').trim()
  const digits = phoneDigits(form.phone_number)
  if (!contactName) errors.name = 'Contact name is required.'
  else if (!REGEX_PERSON_NAME.test(contactName)) errors.name = 'Enter a valid contact name.'
  if (!companyName) errors.companyName = 'Business name is required.'
  else if (companyName.length > COMPANY_NAME_MAX_LENGTH) errors.companyName = 'Business name is too long.'
  else if (!REGEX_COMPANY_NAME.test(companyName)) errors.companyName = 'Enter a valid business name.'
  if (!String(form.email || '').trim()) errors.email = 'Email is required.'
  else if (!REGEX_EMAIL.test(form.email)) errors.email = 'Enter a valid email address.'
  if (!digits) errors.phone_number = 'Phone number is required.'
  else if (digits.length !== 10) errors.phone_number = 'Enter a 10-digit US phone number.'
  if (!form.password) errors.password = 'Password is required.'
  else if (!REGEX_PASSWORD.test(form.password)) errors.password = 'Use 8–20 characters with a mix of letters, numbers, or symbols.'
  if (!form.confirmPassword) errors.confirmPassword = 'Confirm your password.'
  else if (form.confirmPassword !== form.password) errors.confirmPassword = 'Passwords do not match.'
  return errors
}

function FieldError({ id, message }) {
  if (!message) return null
  return (
    <p className="message_error" id={id} role="alert">
      {message}
    </p>
  )
}

function Signup() {
  const history = useHistory()
  useDocumentTitle('Sign up')
  const { form, errors, lifecycleNotice, handleChange, handleSubmit, showSuccessButton, showModal, setShowModal, customTittleText, customMessageModal, cancelButtonText, sucessButtonText, isLoading } = useForm(
    initialForm,
    validationsForm,
  )
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const onFieldChange = (e) => {
    const { name, value } = e.target
    if (name === 'phone_number') {
      handleChange({ target: { name, value: formatUsPhone(value) } })
      return
    }
    handleChange(e)
  }

  return (
    <>
      <div className="login">
        <div className="hero-split">
          <img alt="Tech4Inventory Warehouse" className="hero-image" src={bgAuthenticate} />
        </div>
        <div className="form-split">
          <div className="form-container__signup">
            <div className="form-container_top form-container_top--signup">
              <img alt="Tech4Inventory Logo" className="logo" src={imglogo} />
              <h1 className="title-app">Tech4Inventory</h1>
              <span className="subtitle-app">Create your account</span>
              <p className="information-content__signup">
                Already registered?{' '}
                <button type="button" className="signup-text-link" onClick={() => history.push('/')}>
                  Sign in
                </button>
              </p>
            </div>
            <div className="secondary-actions">
              <GoogleContinueButton purpose="register" />
            </div>
            <form className="form" onSubmit={handleSubmit} noValidate>
              <div className="theinputs">
                <div className="signup-fields-row">
                  <div className="input-group">
                    <label className="label" htmlFor="companyName">
                      Business name
                    </label>
                    <div className="relative-input-wrapper">
                      <span className="input-icon material-symbols-outlined">apartment</span>
                      <input
                        type="text"
                        className={classNames('input', 'input-with-icon', { 'is-invalid': errors.companyName })}
                        name="companyName"
                        id="companyName"
                        placeholder="Acme Auto Repair"
                        value={form.companyName}
                        onChange={onFieldChange}
                        autoComplete="organization"
                        aria-invalid={Boolean(errors.companyName)}
                        aria-describedby={errors.companyName ? 'companyName-error' : undefined}
                      />
                    </div>
                    <FieldError id="companyName-error" message={errors.companyName} />
                  </div>
                  <div className="input-group">
                    <label className="label" htmlFor="name">
                      Contact name
                    </label>
                    <div className="relative-input-wrapper">
                      <span className="input-icon material-symbols-outlined">person</span>
                      <input
                        type="text"
                        className={classNames('input', 'input-with-icon', { 'is-invalid': errors.name })}
                        name="name"
                        id="name"
                        placeholder="Jane Smith"
                        value={form.name}
                        onChange={onFieldChange}
                        autoComplete="name"
                        aria-invalid={Boolean(errors.name)}
                        aria-describedby={errors.name ? 'name-error' : undefined}
                      />
                    </div>
                    <FieldError id="name-error" message={errors.name} />
                  </div>
                </div>
                <div className="input-group">
                  <label className="label" htmlFor="email">
                    Email address
                  </label>
                  <div className="relative-input-wrapper">
                    <span className="input-icon material-symbols-outlined">mail</span>
                    <input
                      type="email"
                      className={classNames('input', 'input-with-icon', { 'is-invalid': errors.email })}
                      name="email"
                      id="email"
                      placeholder="you@company.com"
                      value={form.email}
                      onChange={onFieldChange}
                      autoComplete="email"
                      aria-invalid={Boolean(errors.email)}
                      aria-describedby={errors.email ? 'email-error' : undefined}
                    />
                  </div>
                  <FieldError id="email-error" message={errors.email} />
                </div>
                <div className="input-group">
                  <label className="label" htmlFor="phone_number">
                    Phone number
                  </label>
                  <div className={classNames('signup-phone', { 'is-invalid': errors.phone_number })}>
                    <span className="signup-phone__prefix">+1</span>
                    <input
                      type="tel"
                      className="input signup-phone__input"
                      name="phone_number"
                      id="phone_number"
                      placeholder="(123) 456-7890"
                      value={form.phone_number}
                      onChange={onFieldChange}
                      autoComplete="tel-national"
                      inputMode="numeric"
                      aria-invalid={Boolean(errors.phone_number)}
                      aria-describedby={errors.phone_number ? 'phone_number-error' : undefined}
                    />
                  </div>
                  <FieldError id="phone_number-error" message={errors.phone_number} />
                </div>
                <div className="signup-fields-row">
                  <div className="input-group">
                    <label className="label" htmlFor="password">
                      Password
                    </label>
                    <div className="relative-input-wrapper">
                      <span className="input-icon material-symbols-outlined">lock</span>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        className={classNames('input', 'input-with-icon', 'signup-input--secret', { 'is-invalid': errors.password })}
                        name="password"
                        id="password"
                        placeholder="••••••••"
                        value={form.password}
                        onChange={onFieldChange}
                        autoComplete="new-password"
                        aria-invalid={Boolean(errors.password)}
                        aria-describedby={errors.password ? 'password-error' : 'password-hint'}
                      />
                      <button type="button" className="eye-icon" onClick={() => setShowPassword((prev) => !prev)} aria-label={showPassword ? 'Hide password' : 'Show password'}>
                        <span className="material-symbols-outlined">{showPassword ? 'visibility_off' : 'visibility'}</span>
                      </button>
                    </div>
                    <p className="signup-hint" id="password-hint">
                      8–20 characters, with mixed letters, numbers, or symbols.
                    </p>
                    <FieldError id="password-error" message={errors.password} />
                  </div>
                  <div className="input-group">
                    <label className="label" htmlFor="confirmPassword">
                      Confirm password
                    </label>
                    <div className="relative-input-wrapper">
                      <span className="input-icon material-symbols-outlined">lock</span>
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        className={classNames('input', 'input-with-icon', 'signup-input--secret', { 'is-invalid': errors.confirmPassword })}
                        name="confirmPassword"
                        id="confirmPassword"
                        placeholder="••••••••"
                        value={form.confirmPassword}
                        onChange={onFieldChange}
                        autoComplete="new-password"
                        aria-invalid={Boolean(errors.confirmPassword)}
                        aria-describedby={errors.confirmPassword ? 'confirmPassword-error' : undefined}
                      />
                      <button type="button" className="eye-icon" onClick={() => setShowConfirmPassword((prev) => !prev)} aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}>
                        <span className="material-symbols-outlined">{showConfirmPassword ? 'visibility_off' : 'visibility'}</span>
                      </button>
                    </div>
                    <FieldError id="confirmPassword-error" message={errors.confirmPassword} />
                  </div>
                </div>
                {lifecycleNotice ? <SignupLifecycleNotice code={lifecycleNotice.code} apiMessage={lifecycleNotice.message} email={form.email} /> : null}
                <button className="primary-button" type="submit" disabled={isLoading}>
                  {isLoading && (
                    <div className="spinner-border spinner-border-sm" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  )}
                  {!isLoading && 'Create account'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <CustomModal
        show={showModal}
        customTittleText={customTittleText}
        customMessageModal={customMessageModal}
        onHandleClose={() => setShowModal(false)}
        onHandleSuccess={() => {}}
        isLoading={false}
        showSuccessButton={showSuccessButton}
        actionsFooterStyle={true}
        cancelText={cancelButtonText}
        successText={sucessButtonText}
      />
    </>
  )
}

export { Signup }
export default Signup
